import React, { useState } from "react";
import { Box, Typography, Divider, Button, IconButton } from "@mui/material";
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { atom_listModeState } from '@app/shared/state/list-mode.atom';
import { atom_isDesktop, atom_tourState } from '@app/shared/state/tour.atom';
import { atom_userAddedPlaces, atom_selectedUserPlace, PlaceDetails } from '@app/shared/state/user-added-stops.atom';
import { atom_allBookmarksState, atom_bookmarkedStopsState } from '@app/shared/state/filter.atom';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { atom_modalType, atom_showModalContent, atom_navigationHelperState } from '@app/shared/state/show-content-modal.atom';
import { atom_dropPinMode } from '@app/shared/state/drop-pin-mode.atom';
import { sideBarStateAtom } from '@app/shared/state/sidebar.atom';
import { atom_mapRef } from '@app/shared/state/map-ref.atom';
import { useTranslation } from 'react-i18next';
import DirectionIcon from '@app/new-app/direction.svg';

// User Stop Card component - moved outside to prevent recreation on every render
interface UserStopCardProps {
  place: PlaceDetails;
  isRTL: boolean;
  onShowMore: (place: PlaceDetails) => void;
  onDirections: (e: React.MouseEvent<HTMLButtonElement>, place: PlaceDetails) => void;
  onCardClick: (place: PlaceDetails) => void;
  t: (key: string) => string;
}

const UserStopCard: React.FC<UserStopCardProps> = ({
  place,
  isRTL,
  onShowMore,
  onDirections,
  onCardClick,
  t,
}) => {
  return (
    <Box
      onClick={() => onCardClick(place)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        mb: 1.5,
        cursor: 'pointer',
      }}
    >
      {/* Card Body */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'white',
          borderRadius: '8px',
          boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        {/* Card Content */}
        <Box sx={{ p: 2 }}>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              color: '#333',
              mb: 0.5,
            }}
          >
            {place.name}
          </Typography>
          {place.description && (
            <Typography
              sx={{
                fontSize: '14px',
                color: '#666',
                mb: 0.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {place.description}
            </Typography>
          )}
          {place.formatted_address && (
            <Typography
              sx={{
                fontSize: '12px',
                color: '#999',
              }}
            >
              {place.formatted_address}
            </Typography>
          )}
        </Box>

        <Divider />

        {/* Card Actions */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 1,
          }}
        >
          <Box
            onClick={(e) => { e.stopPropagation(); onShowMore(place); }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#333',
              }}
            >
              {t('new_app.explore_more') || 'Show more'}
            </Typography>
            <i
              className={isRTL ? "fa-solid fa-angle-left" : "fa-solid fa-angle-right"}
              style={{ fontSize: '14px', color: '#333' }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onDirections(e, place); }}
              sx={{ p: 0.5 }}
              aria-label="directions"
            >
              <img src={DirectionIcon} alt="" aria-hidden="true" style={{ width: '21px', height: '21px' }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

interface DesktopAddPlaceContentProps {
  contentRef?: React.RefObject<HTMLDivElement>;
  preventMapInteraction?: (e: React.MouseEvent | React.TouchEvent) => void;
  mapRef?: React.RefObject<any>;
}

export const DesktopAddPlaceContent: React.FC<DesktopAddPlaceContentProps> = ({
  contentRef,
  preventMapInteraction,
  mapRef,
}) => {
  const { t, i18n } = useTranslation();
  const setListMode = useSetRecoilState(atom_listModeState);
  const activeTour = useRecoilValue(atom_tourState);
  const userAddedPlaces = useRecoilValue(atom_userAddedPlaces);
  const [allBookmarks, setAllBookmarks] = useRecoilState(atom_allBookmarksState);
  const bookmarkedStops = useRecoilValue(atom_bookmarkedStopsState);
  const setSelectedUserPlace = useSetRecoilState(atom_selectedUserPlace);
  const setActiveStop = useSetRecoilState(atom_activeStopState);
  const setModalType = useSetRecoilState(atom_modalType);
  const setShowModal = useSetRecoilState(atom_showModalContent);
  const setDropPinMode = useSetRecoilState(atom_dropPinMode);
  const setSideBarState = useSetRecoilState(sideBarStateAtom);
  const setNavState = useSetRecoilState(atom_navigationHelperState);
  const map = useRecoilValue(atom_mapRef);

  // Detect RTL language (Hebrew)
  const isRTL = i18n.language === 'he';

  // Get user added stops for current tour
  const tourId = activeTour?.id;
  const userStops = tourId ? (userAddedPlaces[tourId] || []) : [];

  // Prevent map interaction handler
  const handlePreventMapInteraction = preventMapInteraction || ((e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
  });

  // Close handler
  const handleClose = () => {
    setSideBarState(null);
    setListMode('desktop');
  };

  // Search place handler - opens modal
  const handleSearchPlace = () => {
    setModalType('searchPlace');
    setShowModal(true);
  };

  // Drop pin handler - activates drop pin mode
  const handleDropPin = () => {
    setModalType(null);
    setShowModal(false);
    setDropPinMode({
      isActive: true,
      step: 'positioning',
      pinLocation: null,
    });
    // Close this panel when drop pin mode is active
    handleClose();
  };

  // Check if a place is bookmarked
  const isPlaceSaved = (placeId: string) => {
    return bookmarkedStops.includes(placeId);
  };

  // Toggle bookmark for a user stop
  const handleToggleBookmark = (place: PlaceDetails) => {
    if (!tourId) return;

    const currentTourBookmarks = allBookmarks[tourId] || [];
    if (currentTourBookmarks.includes(place.place_id)) {
      const updatedBookmarks = currentTourBookmarks.filter(id => id !== place.place_id);
      setAllBookmarks({
        ...allBookmarks,
        [tourId]: updatedBookmarks,
      });
    } else {
      setAllBookmarks({
        ...allBookmarks,
        [tourId]: [...currentTourBookmarks, place.place_id],
      });
    }
  };

  // Handle show more for a user stop - always open in dropAPin modal
  const handleShowMore = (place: PlaceDetails) => {
    setSelectedUserPlace(place);
    setDropPinMode({
      isActive: false,
      step: 'naming',
      pinLocation: { lat: place.geometry.location.lat, lng: place.geometry.location.lng },
    });
    setModalType('dropAPin');
    setShowModal(true);
    if (map) {
      map.flyTo([place.geometry.location.lat, place.geometry.location.lng], 17, { duration: 1 });
    }
  };

  // Handle directions for a user stop
  const handleDirections = (e: React.MouseEvent<HTMLButtonElement>, place: PlaceDetails) => {
    e.stopPropagation();
    (window as any).__skipListScroll = true;
    const stopForNav = {
      stop_location: {
        latitude: place.geometry.location.lat,
        longitude: place.geometry.location.lng,
      },
      s_title: place.name,
      location: {
        latitude: place.geometry.location.lat,
        longitude: place.geometry.location.lng,
      },
    };
    setActiveStop(stopForNav as any);
    setNavState({
      anchorEl: e.currentTarget,
      offset: { top: 0, x: -15 },
      usePopover: true
    });
  };

  // State for button press effect
  const [pressedButton, setPressedButton] = useState<string | null>(null);

  // Option row component
  const OptionRow = ({
    icon,
    title,
    onClick,
    id,
  }: {
    icon: React.ReactNode;
    title: string;
    onClick: () => void;
    id: string;
  }) => (
    <Box
      onTouchStart={() => setPressedButton(id)}
      onTouchEnd={(e) => {
        e.preventDefault();
        setPressedButton(null);
        onClick();
      }}
      onMouseDown={() => setPressedButton(id)}
      onMouseUp={() => {
        setPressedButton(null);
        onClick();
      }}
      onMouseLeave={() => setPressedButton(null)}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        px: 1,
        py: 0.5,
        cursor: 'pointer',
        bgcolor: pressedButton === id ? '#E8E8EB' : '#F6F6F9',
        borderRadius: '16px',
        height: '100%',
        border: '0.5px solid #48526B',
        boxShadow: pressedButton === id ? '0px 1px 2px rgba(72, 82, 107, 0.2)' : '0px 2px 4px rgba(72, 82, 107, 0.2)',
        transition: 'transform 0.1s ease, box-shadow 0.1s ease, background-color 0.1s ease',
        transform: pressedButton === id ? 'scale(0.9)' : 'scale(1)',
        '&:hover': {
          bgcolor: pressedButton === id ? '#E8E8EB' : '#ECECEF',
        },
      }}
      role="menuitem"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: 'white',
          border: '1px solid #E0E0E0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 600,
          color: '#333',
          lineHeight: 1.3,
        }}
      >
        {title}
      </Typography>
    </Box>
  );

  return (
    <Box
      dir={isRTL ? 'rtl' : 'ltr'}
      sx={{
        backgroundColor: activeTour?.config?.listColor ? `#${activeTour.config.listColor.replace('#', '')}` : '#F6F6F6',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        textAlign: isRTL ? 'right' : 'left',
        // paddingBottom: '60px',
      }}
      onClick={handlePreventMapInteraction}
      onMouseDown={handlePreventMapInteraction}
      onMouseMove={handlePreventMapInteraction}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: 'row',
        px: 2,
        pt: 1.5,
        pb: 1,
        gap: 1,
      }}>
        {/* "Add Stop" button */}
        <Button
          variant="text"
          onClick={handleDropPin}
          sx={{
            textTransform: 'none',
            color: '#48526B',
            fontSize: '15px',
            fontWeight: 600,
            justifyContent: 'center',
            '&:hover': { bgcolor: 'transparent' },
          }}
        >
          <i className="fa-solid fa-plus" style={{ marginInlineEnd: 8 }} />
          {t('drop_a_pin') || 'Add Stop'}
        </Button>
        {/* Back button */}
        <Button
          onClick={handleClose}
          variant="text"
          disableRipple
          sx={{
            backgroundColor: 'transparent',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '14px',
            minWidth: 'auto',
            p: 0,
            color: '#48526B',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
            },
          }}
        >
          {t('back') || 'Back'}
        </Button>
      </Box>
      {/* Scrollable Content */}
      <Box
        ref={contentRef}
        sx={{
          overflowY: 'auto',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          px: 2,
          pt: 2,
        }}
      >
        <Divider sx={{ color: '#E3E3E3', mb: 2 }} />
        {/* User Added Stops List */}
        {userStops.length > 0 && (
          <Box sx={{ pb: 2 }}>
            {userStops.map((place) => (
              <UserStopCard
                key={place.place_id}
                place={place}
                isRTL={isRTL}
                onShowMore={handleShowMore}
                onDirections={handleDirections}
                onCardClick={(place) => {
                  if (map) {
                    map.flyTo([place.geometry.location.lat, place.geometry.location.lng], 17, { duration: 1 });
                  }
                }}
                t={t}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DesktopAddPlaceContent;
